import { onUnmounted, ref, watch, type ComputedRef, type Ref } from 'vue';

const imageSource = (src: ImageSrcParam): string | null => {
  if (!src) return null;

  if (typeof src !== 'string') return src.value || null;

  return src.trim() || null;
};

type ImageSrcParam =
  | string
  | ComputedRef<string | null | undefined>
  | Ref<string | null | undefined>
  | null
  | undefined;

export function useImageLoader(src: ImageSrcParam) {
  const width = ref<number>(-1);
  const height = ref<number>(-1);
  const isLoading = ref<boolean>(false);
  const isLoaded = ref<boolean>(false);

  const stopWatch = watch(
    () => (src === null || src === undefined ? null : typeof src === 'string' ? src : src.value),
    (newSrc, oldSrc) => {
      if (newSrc === oldSrc || imageSource(newSrc) === null) return;

      isLoaded.value = false;
      isLoading.value = true;

      const img = new Image();

      img.onload = () => {
        width.value = img.width;
        height.value = img.height;
        isLoaded.value = true;
        isLoading.value = false;
      };

      img.src = imageSource(newSrc)!;
    },
    { immediate: true },
  );

  onUnmounted(() => {
    stopWatch();
  });

  return {
    width,
    height,
    isLoading,
    isLoaded,
  };
}
